<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import {
  useItineraryStore,
  usePaxStore,
  usePriceCalendarStore,
  useStatsigEventsStore,
  usePremadeTripStore,
  useCustomerFlowStore,
} from '@apps/main/new-desktop/core/stores';
import { i18n } from '@apps/main/modules/localization/localizationSetup';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { PremadeTripType } from '@apps/main/new-desktop/core/models';
import AITripDescription from '@apps/main/new-desktop/core/components/AITripDescription.vue';

import { AppPortableText, AppRawButton } from '@libs/ui';

import { CarTripPlannerButton } from '../../../../modules/carTripPlannerButton';
import { useTripOverviewPageStore } from '../../stores';

import TripOverviewHeaderSummaryButton from './TripOverviewHeaderSummaryButton.vue';
import { getHeaderSummarySegments } from './TripOverviewHeaderMobile.helper';
import type { TripSummaryHeader } from './TripOverviewHeaderMobile.helper';
import PremadeTripHeaderMobile from './PremadeTripHeaderMobile.vue';

interface TripOverviewHeaderMobileEmits {
  (eventName: 'pax-click'): void;
  (eventName: 'edit-trip'): void;
  (eventName: 'map-click'): void;
  (eventName: 'home-airport-click'): void;
  (eventName: 'months-selector-click'): void;
  (eventName: 'open-premade-trip'): void;
  (eventName: 'select-itinerary-month'): void;
  (eventName: 'navigate-to-car-trip'): void;
}

const emit = defineEmits<TripOverviewHeaderMobileEmits>();

const { t } = i18n.global;

const statsigEventsStore = useStatsigEventsStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const paxStore = usePaxStore();
const priceCalendarStore = usePriceCalendarStore();
const itineraryStore = useItineraryStore();
const recipeStore = useRecipeStore();
const premadeTripStore = usePremadeTripStore();
const customerFlowStore = useCustomerFlowStore();

const tripSummaryHeaderSegments = computed<TripSummaryHeader[]>(() =>
  getHeaderSummarySegments(recipeStore)
);

const isCarTripPlannerUser = customerFlowStore.isCarTripFlow;

const monthLabel = computed<string>(() =>
  itineraryStore.userSelectedItinerary === undefined
    ? `${t('planner.flexible_in').toLocaleLowerCase()} ${
        priceCalendarStore.calendarTriggerText
      }`
    : priceCalendarStore.calendarTriggerText
);

const firstDestinationName = computed<string | undefined>(() =>
  tripOverviewPageStore.getFirstDestinationName()
);

const firstDestinationNameSaved = ref<string | undefined>(undefined);

watch(
  () => firstDestinationName.value,
  (newValue) => {
    if (newValue && newValue !== firstDestinationNameSaved.value) {
      firstDestinationNameSaved.value = newValue;
    }
  },
  { immediate: true }
);

const firstDestinationNameDisplay = computed<string>(() => {
  if (
    tripOverviewPageStore.numberOfDestinations === 1 &&
    firstDestinationNameSaved.value
  ) {
    return firstDestinationNameSaved.value;
  }

  return `${tripOverviewPageStore.numberOfDestinations} ${t(
    'planner.destination',
    tripOverviewPageStore.numberOfDestinations
  ).toLowerCase()}`;
});

function openHomeAirportModal(): void {
  statsigEventsStore.logTripOverviewHeaderActionClicked({
    actionType: 'homeAirport',
  });

  emit('home-airport-click');
}

function openEditTripModal(actionType: 'editTrip' | 'editStay'): void {
  statsigEventsStore.logTripOverviewHeaderActionClicked({
    actionType,
  });
  emit('edit-trip');
}

function openPaxSelectorModal(): void {
  statsigEventsStore.logTripOverviewHeaderActionClicked({
    actionType: 'paxSelector',
  });

  emit('pax-click');
}

function openMonthsSelectorModal(): void {
  statsigEventsStore.logTripOverviewHeaderActionClicked({
    actionType: 'monthsSelector',
  });

  emit('months-selector-click');
}

function openPremadeTripDetailsModal(): void {
  emit('open-premade-trip');
}
</script>

<template>
  <div class="trip-overview-header-mobile">
    <PremadeTripHeaderMobile
      v-if="premadeTripStore.premadeTrip"
      :trip-details="premadeTripStore.premadeTrip"
      @show-full-description="openPremadeTripDetailsModal"
    >
      <template #description>
        <AppPortableText
          v-if="
            premadeTripStore.premadeTrip.type ===
            PremadeTripType.PublishedInSanity
          "
          :portable-text="premadeTripStore.premadeTrip.description"
          type="premade-trip"
        />
        <AITripDescription
          v-if="premadeTripStore.premadeTrip.type === PremadeTripType.AI"
          :description="premadeTripStore.premadeTrip.description"
        />
      </template>
    </PremadeTripHeaderMobile>
    <CarTripPlannerButton v-if="!!isCarTripPlannerUser" />
    <TripOverviewHeaderSummaryButton
      :calendar-text="monthLabel"
      :number-of-nights="tripOverviewPageStore.numberOfNights"
      :number-of-pax="paxStore.numberOfTravelers"
      :header-segments="tripSummaryHeaderSegments"
    >
      <div class="buttons">
        <AppRawButton
          v-if="tripOverviewPageStore.homeFlightData"
          class="button"
          @click="openHomeAirportModal"
        >
          <div class="button-wrapper">
            <div class="label">
              <span class="title">{{ $t('planner.from') }}</span>
              <span class="description">
                {{ tripOverviewPageStore.homeFlightData.airport.name }}
              </span>
            </div>
          </div>
        </AppRawButton>
        <AppRawButton class="button" @click="openEditTripModal('editTrip')">
          <div class="button-wrapper">
            <div class="label">
              <span class="title">{{ $t('planner.where') }}</span>
              <span class="description">
                <span>
                  {{ firstDestinationNameDisplay }}
                </span>
              </span>
            </div>
          </div>
        </AppRawButton>
        <AppRawButton class="button" @click="openPaxSelectorModal">
          <div class="button-wrapper">
            <div class="label">
              <span class="title">
                {{ $t('planner.pax.travelers', paxStore.numberOfTravelers) }}
              </span>
              <span class="description">
                {{ paxStore.getPaxesButtonDetailedText }}
              </span>
            </div>
          </div>
        </AppRawButton>
        <AppRawButton class="button" @click="openMonthsSelectorModal">
          <div class="button-wrapper">
            <div class="label">
              <span class="title">{{ $t('planner.when') }}</span>
              <span class="description">
                {{ monthLabel }}
              </span>
            </div>
          </div>
        </AppRawButton>
        <AppRawButton class="button" @click="openEditTripModal('editStay')">
          <div class="button-wrapper">
            <div class="label">
              <span class="title">{{ $t('planner.trip_length') }}</span>
              <span class="description">
                {{ tripOverviewPageStore.numberOfNights }}
                {{
                  $t(
                    'planner.nights',
                    tripOverviewPageStore.numberOfNights
                  ).toLowerCase()
                }}
              </span>
            </div>
          </div>
        </AppRawButton>
      </div>
    </TripOverviewHeaderSummaryButton>
  </div>
</template>

<style scoped lang="scss">
.trip-overview-header-mobile {
  .car-trip-button {
    margin: 0 var(--spacing-16) var(--spacing-16);

    @include for-less-or-equal-than-599-px {
      margin: 0 auto var(--spacing-12);
    }
  }

  .buttons {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .button {
      border: 1px solid var(--color-neutral-30);
      border-radius: var(--border-radius-8);
      padding: var(--spacing-12) var(--spacing-16);

      &:first-child {
        margin-top: var(--spacing-16);
      }

      .button-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .label {
          display: flex;
          flex-direction: column;
          align-items: flex-start;

          .title {
            @include font-label-m-bold;
          }

          .description {
            @include font-label-m-light;
          }
        }
      }
    }
  }
}
</style>
